/**
 * CommentPanel — Universal comment panel that can attach to any entity
 * Features: threaded comments, add/edit/delete, replies, internal comments, @mention autocomplete
 *
 * Two render variants:
 * - 'panel' (default): the original fixed slide-over, used by Punch List /
 *   Daily Log / Change Order / RFI / Subcontract / Invoice / Submittal / Bill
 *   / Estimate pages via useCommentPanel.
 * - 'inline': no fixed chrome, no header/close button — drops the list +
 *   composer into a parent's own layout. Used by the task peek Activity
 *   section and mobile task detail, which render their own "Activity · N"
 *   label and own the surrounding card. Task comments (entityType==='task')
 *   ALWAYS use 'inline' + the dedicated /tasks/{id}/comments endpoints
 *   (own table, own auto-logged system events, ID-keyed mentions) rather
 *   than the generic polymorphic /comments endpoint — see
 *   docs/features/task-attachments-and-comments.md.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiGet, apiPost, apiPatch, apiDelete, apiBaseUrl, authedFetch, type TaskComment } from '@/lib/api'
import { formatDateWithOptions, formatTimeWithOptions } from '../../utils/dateUtils'
import { tokens } from '@/design-system/tokens'
import { Button } from '@/components/atoms/Button'
import { Spinner } from '@/components/atoms/Spinner'
import { useToast } from '@/components/molecules/Toast'
import { formatDistanceToNow } from 'date-fns'
import { Textarea } from '@/components/atoms/Textarea'
import {
  X, MessageCircle, Send, Reply, Edit2, Trash2,
  Lock, ChevronDown, ChevronUp, AtSign, Paperclip, Download, FileText,
} from 'lucide-react'
import { AttachmentViewer, toViewerAttachment } from '@/components/task/shared/AttachmentViewer'
import {
  type Attachment as TaskAttachment, attachmentName, attachmentThumbUrl, attachmentUrl, isImageAttachment,
} from '@/components/task/shared/attachments'

interface Comment {
  id: number
  entity_type: string
  entity_id: number
  user_id?: number
  parent_id?: number
  body: string
  attachments?: TaskAttachment[]
  is_internal: boolean
  created_at: string
  updated_at: string
  user_name?: string
  user_email?: string
  replies?: Comment[]
  reply_count?: number
  /** Task comments only: 'event' rows are auto-logged (status/field changes), read-only. */
  kind?: 'comment' | 'event'
}

interface MentionUser {
  id: number
  name: string
  email: string
}

interface CommentPanelProps {
  entityType: string
  entityId: number | string
  isOpen: boolean
  onClose: () => void
  title?: string
  /** 'panel' (default) = fixed slide-over with its own header/close button.
   *  'inline' = no chrome, drops list+composer into the parent's layout. */
  variant?: 'panel' | 'inline'
  /** Only relevant for variant='inline'. Bumps touch targets/type scale to
   *  match the mobile task-detail card instead of the desktop peek. */
  size?: 'web' | 'mobile'
  /** Gates the composer. Panel-mode pages never pass this (always true);
   *  the task peek/mobile pass their own read-only state. */
  canEdit?: boolean
  /** Shows the attach-file button and renders attachments on comments.
   *  Only wired for entityType==='task' today (own upload endpoint). */
  allowAttachments?: boolean
  /** Bump to force an immediate refetch (e.g. after a flag action logs a
   *  new auto event server-side) without waiting for the poll interval. */
  refreshSignal?: number
  /** Reports the current top-level comment count so the parent's own
   *  "Activity · N" label (inline mode never renders its own header) stays accurate. */
  onCountChange?: (count: number) => void
}

// Render a comment body with @mentions highlighted as gold pills
function renderMentions(body: string): React.ReactNode {
  const parts = body.split(/(@\w+)/g)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} style={{
        display: 'inline-block',
        background: `${tokens.color.brand.gold}22`,
        color: tokens.color.brand.gold,
        borderRadius: '4px',
        padding: '0 4px',
        fontSize: 'inherit',
        fontWeight: 600
      }}>
        {part}
      </span>
    ) : part
  )
}

// Matches the design brief's activity timestamp convention (task-peek-web.jsx
// / task-detail-mobile.jsx both use "8:12 AM" / "Yesterday" / "Jul 3", not a
// relative "3 minutes ago" string) — shared so web + mobile task activity
// render identically instead of drifting (mobile previously always showed a
// bare time with no day fallback).
function inlineCommentTimeLabel(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return formatTimeWithOptions(d, { hour: 'numeric', minute: '2-digit' })
  }
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return formatDateWithOptions(d, { month: 'short', day: 'numeric' })
}

async function uploadTaskCommentAttachment(taskId: number | string, file: File): Promise<TaskAttachment> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await authedFetch(`/api/v1/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: formData,
  }, { redirectOnAuthFailure: false })
  if (!res.ok) throw new Error(`Upload failed (${res.status})`)
  return res.json()
}

function taskCommentToComment(c: TaskComment): Comment {
  return {
    id: c.id,
    entity_type: 'task',
    entity_id: 0,
    user_id: c.user_id ?? undefined,
    body: c.body,
    is_internal: false,
    created_at: c.created_at,
    updated_at: c.created_at,
    user_name: c.user_name,
    replies: [],
    reply_count: 0,
    kind: c.kind,
    attachments: (c.attachments as TaskAttachment[] | undefined) ?? [],
  }
}

export function CommentPanel({
  entityType, entityId, isOpen, onClose, title,
  variant = 'panel', size = 'web', canEdit = true, allowAttachments = false,
  refreshSignal, onCountChange,
}: CommentPanelProps) {
  const { success, error } = useToast()
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set())
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const inlineInputRef = useRef<HTMLInputElement>(null)

  const isTask = entityType === 'task'
  const inline = variant === 'inline'
  const mobile = size === 'mobile'

  // @mention state
  const [users, setUsers] = useState<MentionUser[]>([])
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionSearch, setMentionSearch] = useState('')
  const [mentionPos, setMentionPos] = useState(0)
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([])
  const mentionDropdownRef = useRef<HTMLDivElement>(null)

  // Task-mode attachments
  const [pendingAttachments, setPendingAttachments] = useState<TaskAttachment[]>([])
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [previewAttachment, setPreviewAttachment] = useState<TaskAttachment | null>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  // Load users for @mention
  useEffect(() => {
    if (isOpen) {
      apiGet('/users?limit=100').then((res: any) => {
        setUsers((res.data || res || []).filter((u: MentionUser) => u.name))
      }).catch(() => {})
    }
  }, [isOpen])

  const fetchComments = useCallback(async () => {
    try {
      if (isTask) {
        const res = await apiGet(`/tasks/${entityId}/comments`) as { comments?: TaskComment[] }
        setComments((res.comments || []).map(taskCommentToComment))
      } else {
        const res = await apiGet(`/comments?entity_type=${entityType}&entity_id=${entityId}`)
        setComments((res as any).data || [])
      }
    } catch (e) {
      // Do NOT clear comments on a transient fetch error. A background poll /
      // refetch that fails on flaky mobile signal would otherwise wipe the
      // user's visible (and already-persisted) comments — "comments disappear
      // a few seconds after posting" (#2182). Keep the last-good list; the next
      // successful poll reconciles. Initial-load errors leave the initial [].
      console.warn('[CommentPanel] comment refetch failed; keeping existing list', e)
    } finally {
      setLoading(false)
    }
  }, [isTask, entityType, entityId])

  useEffect(() => {
    if (isOpen && entityId) {
      setLoading(true)
      fetchComments()
    }
  }, [isOpen, entityType, entityId, fetchComments])

  const skipNextRefreshSignal = useRef(true)
  useEffect(() => {
    if (refreshSignal === undefined) return
    if (skipNextRefreshSignal.current) { skipNextRefreshSignal.current = false; return }
    if (isOpen && entityId) fetchComments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal])

  useEffect(() => {
    onCountChange?.(comments.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments.length])

  // Poll + refetch on tab focus (mirrors the bespoke composers this replaces).
  useEffect(() => {
    if (!isOpen || !entityId) return
    const interval = setInterval(() => { void fetchComments() }, 20000)
    const onFocusOrVisible = () => { if (document.visibilityState === 'visible') void fetchComments() }
    document.addEventListener('visibilitychange', onFocusOrVisible)
    window.addEventListener('focus', onFocusOrVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onFocusOrVisible)
      window.removeEventListener('focus', onFocusOrVisible)
    }
  }, [isOpen, entityId, fetchComments])

  // Handle @mention detection in textarea
  const handleBodyChange = useCallback((val: string) => {
    setNewComment(val)
    const atIdx = val.lastIndexOf('@')
    if (atIdx >= 0) {
      const afterAt = val.slice(atIdx + 1)
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        setMentionSearch(afterAt.toLowerCase())
        setMentionPos(atIdx)
        setShowMentionDropdown(true)
        return
      }
    }
    setShowMentionDropdown(false)
  }, [])

  const insertMention = useCallback((user: MentionUser) => {
    const firstName = user.name.split(' ')[0]
    const newBody = newComment.slice(0, mentionPos) + '@' + firstName + ' ' + newComment.slice(mentionPos + 1 + mentionSearch.length)
    setNewComment(newBody)
    setShowMentionDropdown(false)
    setMentionedUsers(prev => prev.some(u => u.id === user.id) ? prev : [...prev, user])
    setTimeout(() => { inputRef.current?.focus(); inlineInputRef.current?.focus() }, 0)
  }, [newComment, mentionPos, mentionSearch])

  const filteredMentionUsers = users.filter(u =>
    u.name.toLowerCase().includes(mentionSearch) ||
    u.name.split(' ')[0].toLowerCase().startsWith(mentionSearch)
  ).slice(0, 6)

  const handleAttachmentFile = useCallback(async (file: File) => {
    setUploadingAttachment(true)
    try {
      const att = await uploadTaskCommentAttachment(entityId, file)
      setPendingAttachments(prev => [...prev, att])
    } catch {
      error('Attachment upload failed')
    } finally {
      setUploadingAttachment(false)
    }
  }, [entityId, error])

  const removePendingAttachment = useCallback((id: number) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== id))
  }, [])

  const handleSubmit = async () => {
    if (!newComment.trim()) return
    setSubmitting(true)
    try {
      const body = newComment.trim()
      if (isTask) {
        const mentions = mentionedUsers
          .filter(u => body.toLowerCase().includes('@' + u.name.split(' ')[0].toLowerCase()))
          .map(u => u.id)
        const res = await apiPost(`/tasks/${entityId}/comments`, {
          body, mentions, attachments: pendingAttachments,
        }) as TaskComment
        setComments(prev => [...prev, taskCommentToComment(res)])
        setPendingAttachments([])
        setMentionedUsers([])
      } else {
        const mentionMatches = body.match(/@(\w+)/g) || []
        const mentions = mentionMatches.map(m => m.slice(1))
        await apiPost('/comments', {
          entity_type: entityType,
          entity_id: entityId,
          body,
          is_internal: isInternal,
          mentions
        })
        fetchComments()
      }
      setNewComment('')
      setShowMentionDropdown(false)
      success('Comment added')
    } catch (e) {
      error('Failed to add comment')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = async (parentId: number) => {
    if (!replyText.trim()) return
    setSubmitting(true)
    try {
      const mentionMatches = replyText.match(/@(\w+)/g) || []
      const mentions = mentionMatches.map(m => m.slice(1))
      await apiPost('/comments', {
        entity_type: entityType,
        entity_id: entityId,
        parent_id: parentId,
        body: replyText.trim(),
        is_internal: isInternal,
        mentions
      })
      setReplyText('')
      setReplyingTo(null)
      fetchComments()
      success('Reply added')
    } catch (e) {
      error('Failed to add reply')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (id: number) => {
    if (!editText.trim()) return
    try {
      await apiPatch(`/comments/${id}`, { body: editText.trim() })
      setEditingId(null)
      setEditText('')
      fetchComments()
      success('Comment updated')
    } catch (e) {
      error('Failed to update comment')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this comment?')) return
    try {
      await apiDelete(`/comments/${id}`)
      fetchComments()
      success('Comment deleted')
    } catch (e) {
      error('Failed to delete comment')
    }
  }

  const toggleReplies = (id: number) => {
    const newSet = new Set(expandedReplies)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setExpandedReplies(newSet)
  }

  if (!isOpen) return null

  // ── Attachment chips (task mode only) — shared between pending (composer)
  // and posted (comment body) rendering. Images open AttachmentViewer in-app;
  // everything else is an icon + filename + explicit Download, never a nav-away.
  const renderAttachmentChip = (a: TaskAttachment, onRemove?: () => void) => {
    const isImg = isImageAttachment(a)
    return (
      <div key={a.id} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        border: `1px solid ${tokens.color.border.default}`, borderRadius: tokens.radius.sm,
        padding: '4px 8px', fontSize: 12, background: tokens.color.background.elevated,
      }}>
        {isImg ? (
          <button
            type="button" onClick={() => setPreviewAttachment(a)}
            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
            title={attachmentName(a)}
          >
            <img src={attachmentThumbUrl(a)} alt={attachmentName(a)}
              style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }}/>
          </button>
        ) : (
          <FileText size={14} style={{ color: tokens.color.text.muted, flexShrink: 0 }}/>
        )}
        <span style={{
          maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: tokens.color.text.secondary,
        }}>{attachmentName(a)}</span>
        {!isImg && (
          <a href={attachmentUrl(a)} download={attachmentName(a)} title="Download"
            style={{ color: tokens.color.text.muted, display: 'flex' }}>
            <Download size={13}/>
          </a>
        )}
        {onRemove && (
          <button type="button" onClick={onRemove} aria-label="Remove attachment"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: tokens.color.text.muted, display: 'flex' }}>
            <X size={13}/>
          </button>
        )}
      </div>
    )
  }

  const renderComment = (comment: Comment, isReply = false) => {
    const isEditing = editingId === comment.id
    const isReplying = replyingTo === comment.id
    const hasReplies = (comment.replies?.length || 0) > 0 || (comment.reply_count || 0) > 0
    const showReplies = expandedReplies.has(comment.id)
    const isEvent = comment.kind === 'event'

    return (
      <div
        key={comment.id}
        style={{
          padding: isReply ? '8px 12px' : '12px 16px',
          marginLeft: isReply ? '24px' : 0,
          borderLeft: isReply ? `2px solid ${tokens.color.border.default}` : 'none',
          marginBottom: '8px',
          background: comment.is_internal ? `${tokens.color.brand.gold}08` : 'transparent',
          borderRadius: tokens.radius.md
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: tokens.color.brand.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--bg-page)'
          }}>
            {(comment.user_name || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600, fontSize: '13px', color: tokens.color.text.primary }}>
              {comment.user_name || 'Unknown'}
            </span>
            <span style={{ fontSize: '11px', color: tokens.color.text.muted, marginLeft: '8px' }}>
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </span>
            {comment.is_internal && (
              <span style={{
                marginLeft: '8px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: `${tokens.color.brand.gold}22`,
                color: tokens.color.brand.gold,
                fontSize: '10px',
                fontWeight: 600
              }}>
                <Lock size={10} style={{ marginRight: '2px', verticalAlign: 'middle' }} />
                INTERNAL
              </span>
            )}
          </div>
          {!isTask && comment.user_id && (
            <div style={{ display: 'flex', gap: '4px' }}>
              <Button
                onClick={() => { setEditingId(comment.id); setEditText(comment.body) }}
                style={{
                  padding: '4px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: tokens.color.text.muted
                }}
                title="Edit"
              >
                <Edit2 size={14} />
              </Button>
              <Button
                onClick={() => handleDelete(comment.id)}
                style={{
                  padding: '4px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: tokens.color.text.muted
                }}
                title="Delete"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          )}
        </div>

        {/* Body */}
        {isEditing ? (
          <div style={{ marginBottom: '8px' }}>
            <Textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: tokens.radius.sm,
                background: tokens.color.background.elevated,
                color: tokens.color.text.primary,
                fontSize: '13px',
                minHeight: '60px',
                resize: 'vertical',
                fontFamily: tokens.typography.fontFamily.sans
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button size="sm" onClick={() => handleEdit(comment.id)}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p style={{
            fontSize: '13px',
            color: isEvent ? tokens.color.text.muted : tokens.color.text.secondary,
            whiteSpace: 'pre-wrap',
            margin: '0 0 8px 0',
            lineHeight: 1.5
          }}>
            {renderMentions(comment.body)}
          </p>
        )}

        {/* Comment attachments (task mode) */}
        {isTask && !isEvent && comment.attachments && comment.attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {comment.attachments.map(a => renderAttachmentChip(a))}
          </div>
        )}

        {/* Actions */}
        {!isTask && !isEditing && !isReply && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              onClick={() => { setReplyingTo(isReplying ? null : comment.id); setReplyText('') }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                color: tokens.color.text.muted
              }}
            >
              <Reply size={14} />
              Reply
            </Button>
            {hasReplies && (
              <Button
                onClick={() => toggleReplies(comment.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: tokens.color.brand.gold
                }}
              >
                {showReplies ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {comment.reply_count || comment.replies?.length || 0} {(comment.reply_count || comment.replies?.length || 0) === 1 ? 'reply' : 'replies'}
              </Button>
            )}
          </div>
        )}

        {/* Reply Form */}
        {isReplying && (
          <div style={{ marginTop: '8px' }}>
            <Textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: tokens.radius.sm,
                background: tokens.color.background.elevated,
                color: tokens.color.text.primary,
                fontSize: '13px',
                minHeight: '60px',
                resize: 'vertical',
                fontFamily: tokens.typography.fontFamily.sans
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button
                size="sm"
                onClick={() => handleReply(comment.id)}
                disabled={submitting || !replyText.trim()}
              >
                <Send size={14} style={{ marginRight: '4px' }} />
                Reply
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReplyingTo(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Replies */}
        {showReplies && comment.replies && comment.replies.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {comment.replies.map(reply => renderComment(reply, true))}
          </div>
        )}
      </div>
    )
  }

  // ── Inline variant — matches the task peek / mobile task-detail's own
  // pill-composer + avatar-timeline visual treatment (CSS custom props,
  // not the `tokens` JS object the panel variant uses for desktop pages).
  if (inline) {
    // Composer type scale (input text + icon sizing) — independent of the
    // timeline's own type scale below, per the design briefs: mobile input
    // is 15px/46px tall, but mobile timeline body text is 13.5px, not 15.
    const composerFontSize = mobile ? 15 : 13
    const btnSize = mobile ? 46 : 38
    // Timeline type scale — task-detail-mobile.jsx:96-104 vs task-peek-web.jsx:543-546.
    const avatarSize = mobile ? 28 : 26
    const avatarFontSize = mobile ? 10.5 : 10
    const bodyFontSize = mobile ? 13.5 : 13
    const timestampFontSize = mobile ? 11 : 10.5
    const timestampMarginLeft = mobile ? 7 : 6
    const rowGap = mobile ? 10 : 9
    const listGap = mobile ? 13 : 11
    const emptyLabel = isTask ? 'No activity yet.' : 'No comments yet.'
    // Web renders composer-first (task-peek-web.jsx:536), mobile renders
    // list-first (task-detail-mobile.jsx:95) — the two briefs deliberately
    // differ here; base `ActivitySection` already had the web order right.
    const composerOnTop = !mobile
    // Spacing between the two blocks: web's condition (task-peek-web.jsx:537)
    // lives on the composer and keys off whether the list has content; mobile's
    // (task-detail-mobile.jsx:94) lives on the list and keys off whether the
    // composer will render at all.
    const listMarginBottom = !composerOnTop && canEdit ? 14 : 0

    const list = (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: listGap,
        marginBottom: listMarginBottom,
      }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <Spinner />
          </div>
        ) : comments.length === 0 ? (
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: bodyFontSize - 0.5, color: 'var(--text-dim)' }}>
            {emptyLabel}
          </div>
        ) : (
          comments.map(c => {
            const isEvent = c.kind === 'event'
            return (
              <div key={c.id} style={{ display: 'flex', gap: rowGap, alignItems: 'flex-start' }}>
                <span style={{
                  width: avatarSize, height: avatarSize, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: avatarFontSize, fontWeight: 700, fontFamily: 'var(--font-sans)',
                }}>{(c.user_name || '?').slice(0, 2).toUpperCase()}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontFamily: 'var(--font-sans)', fontSize: bodyFontSize, fontWeight: 700,
                    color: 'var(--text-primary)',
                  }}>{c.user_name}</span>
                  <span style={{
                    fontFamily: 'var(--font-sans)', fontSize: bodyFontSize,
                    color: isEvent ? 'var(--text-muted)' : 'var(--text-secondary)',
                  }}> {renderMentions(c.body)}</span>
                  <span style={{
                    marginLeft: timestampMarginLeft, fontSize: timestampFontSize, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
                  }}>{inlineCommentTimeLabel(c.created_at)}</span>
                  {!isEvent && c.attachments && c.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                      {c.attachments.map(a => (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
                          padding: '3px 7px', fontSize: bodyFontSize - 1.5,
                        }}>
                          {isImageAttachment(a) ? (
                            <button type="button" onClick={() => setPreviewAttachment(a)}
                              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}>
                              <img src={attachmentThumbUrl(a)} alt={attachmentName(a)}
                                style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }}/>
                            </button>
                          ) : (
                            <FileText size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
                          )}
                          <span style={{
                            maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            color: 'var(--text-secondary)',
                          }}>{attachmentName(a)}</span>
                          <a href={attachmentUrl(a)} download={attachmentName(a)} title="Download"
                            style={{ color: 'var(--text-dim)', display: 'flex' }}>
                            <Download size={12}/>
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    )

    const composer = canEdit && (
      <div style={{ position: 'relative', marginBottom: composerOnTop && comments.length ? 14 : 0 }}>
        {showMentionDropdown && filteredMentionUsers.length > 0 && (
          <div
            ref={mentionDropdownRef}
            style={composerOnTop ? {
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              zIndex: 10, overflow: 'hidden', marginTop: 6,
            } : {
              position: 'absolute', bottom: '100%', left: 0, right: 0,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)', boxShadow: '0 -4px 12px rgba(0,0,0,0.25)',
              zIndex: 10, overflow: 'hidden', marginBottom: 6,
            }}
          >
            {filteredMentionUsers.map(user => (
              <button
                key={user.id}
                onMouseDown={e => { e.preventDefault(); insertMention(user) }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '8px 12px', background: 'transparent', border: 'none',
                  cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)',
                  fontSize: composerFontSize - 1, fontFamily: 'var(--font-sans)',
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-gold)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: 'var(--brand-gold-ink)', flexShrink: 0,
                }}>{user.name.charAt(0).toUpperCase()}</span>
                <span>{user.name}</span>
              </button>
            ))}
          </div>
        )}

        {pendingAttachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {pendingAttachments.map(a => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)', padding: '3px 7px', fontSize: composerFontSize - 2,
              }}>
                {isImageAttachment(a) ? (
                  <img src={attachmentThumbUrl(a)} alt={attachmentName(a)}
                    style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4 }}/>
                ) : (
                  <FileText size={13} style={{ color: 'var(--text-muted)' }}/>
                )}
                <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachmentName(a)}
                </span>
                <button type="button" onClick={() => removePendingAttachment(a.id)} aria-label="Remove attachment"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dim)', display: 'flex' }}>
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {allowAttachments && (
            <>
              <input
                ref={attachmentInputRef} type="file" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) void handleAttachmentFile(f); e.target.value = '' }}
              />
              <button
                type="button" onClick={() => attachmentInputRef.current?.click()}
                disabled={uploadingAttachment}
                aria-label="Attach a file"
                style={{
                  width: btnSize, height: btnSize, borderRadius: '50%', flexShrink: 0,
                  border: '1.5px solid var(--border)', background: 'var(--bg-page)', cursor: 'pointer',
                  color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {uploadingAttachment ? <Spinner size="sm" /> : <Paperclip size={mobile ? 18 : 16} strokeWidth={2} />}
              </button>
            </>
          )}
          <input
            ref={inlineInputRef}
            value={newComment}
            onChange={e => handleBodyChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setShowMentionDropdown(false); return }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSubmit() }
            }}
            placeholder={mobile ? 'Add a comment…' : 'Write a comment…  (Enter to post)'}
            disabled={submitting}
            style={{
              flex: 1, minHeight: btnSize, padding: '0 13px',
              borderRadius: 999, border: '1.5px solid var(--border)',
              background: 'var(--bg-page)', outline: 'none',
              fontFamily: 'var(--font-sans)', fontSize: composerFontSize,
              color: 'var(--text-primary)',
            }}
          />
          <button
            type="button" onClick={() => void handleSubmit()} disabled={submitting || !newComment.trim()}
            aria-label="Post comment"
            style={{
              width: btnSize, height: btnSize, borderRadius: '50%', flexShrink: 0,
              border: 'none', cursor: newComment.trim() ? 'pointer' : 'default',
              background: newComment.trim() ? 'var(--brand-gold)' : 'var(--bg-elevated)',
              color: newComment.trim() ? 'var(--brand-gold-ink)' : 'var(--text-dim)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
            {submitting ? <Spinner size="sm" /> : <Send size={mobile ? 20 : 18} strokeWidth={2.4} />}
          </button>
        </div>
      </div>
    )

    return (
      <div style={{ position: 'relative' }}>
        <AttachmentViewer
          attachment={previewAttachment ? toViewerAttachment(previewAttachment) : null}
          onClose={() => setPreviewAttachment(null)}
        />
        {composerOnTop ? (<>{composer}{list}</>) : (<>{list}{composer}</>)}
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '400px',
        maxWidth: '100vw',
        height: '100vh',
        background: tokens.color.background.surface,
        borderLeft: `1px solid ${tokens.color.border.default}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        boxShadow: '-4px 0 20px rgba(0,0,0,0.4)'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: `1px solid ${tokens.color.border.default}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageCircle size={20} style={{ color: tokens.color.brand.gold }} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: tokens.color.text.primary }}>
            {title || 'Comments'}
          </h3>
          <span style={{
            fontSize: '12px',
            color: tokens.color.text.muted,
            background: tokens.color.background.elevated,
            padding: '2px 8px',
            borderRadius: '12px'
          }}>
            {comments.length}
          </span>
        </div>
        <Button
          onClick={onClose}
          style={{
            padding: '8px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: tokens.color.text.muted
          }}
        >
          <X size={20} />
        </Button>
      </div>

      {/* Comments List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
      }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner />
          </div>
        ) : comments.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: tokens.color.text.muted
          }}>
            <MessageCircle size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
            <p>No comments yet</p>
            <p style={{ fontSize: '12px' }}>Be the first to comment!</p>
          </div>
        ) : (
          comments.map(comment => renderComment(comment))
        )}
      </div>

      {/* New Comment Form */}
      <div style={{
        padding: '16px',
        borderTop: `1px solid ${tokens.color.border.default}`,
        background: tokens.color.background.elevated,
        position: 'relative'
      }}>
        {/* @mention dropdown */}
        {showMentionDropdown && filteredMentionUsers.length > 0 && (
          <div
            ref={mentionDropdownRef}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '16px',
              right: '16px',
              background: tokens.color.background.surface,
              border: `1px solid ${tokens.color.border.default}`,
              borderRadius: tokens.radius.md,
              boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
              zIndex: 10,
              overflow: 'hidden',
              marginBottom: '4px'
            }}
          >
            {filteredMentionUsers.map(user => (
              <button
                key={user.id}
                onMouseDown={e => { e.preventDefault(); insertMention(user) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: tokens.color.text.primary,
                  fontSize: '13px',
                  borderBottom: `1px solid ${tokens.color.border.default}`
                }}
                onMouseEnter={e => (e.currentTarget.style.background = tokens.color.background.elevated)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: tokens.color.brand.gold,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 700, color: 'var(--bg-page)', flexShrink: 0
                }}>
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>{user.name}</div>
                  <div style={{ fontSize: '11px', color: tokens.color.text.muted }}>{user.email}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <Textarea
            ref={inputRef}
            value={newComment}
            onChange={e => handleBodyChange(e.target.value)}
            placeholder="Write a comment... (type @ to mention someone)"
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: '36px',
              border: `1px solid ${tokens.color.border.default}`,
              borderRadius: tokens.radius.md,
              background: tokens.color.background.surface,
              color: tokens.color.text.primary,
              fontSize: '13px',
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: tokens.typography.fontFamily.sans,
              marginBottom: '12px'
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setShowMentionDropdown(false); return }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                handleSubmit()
              }
            }}
          />
          <AtSign
            size={14}
            style={{
              position: 'absolute',
              right: '10px',
              top: '12px',
              color: tokens.color.text.muted,
              pointerEvents: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: tokens.color.text.muted,
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={isInternal}
              onChange={e => setIsInternal(e.target.checked)}
              style={{ accentColor: tokens.color.brand.gold }}
            />
            <Lock size={12} />
            Internal only
          </label>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !newComment.trim()}
          >
            {submitting ? <Spinner size="sm" /> : (
              <>
                <Send size={14} style={{ marginRight: '6px' }} />
                Post
              </>
            )}
          </Button>
        </div>
        <p style={{ fontSize: '10px', color: tokens.color.text.muted, marginTop: '8px' }}>
          Press Cmd+Enter to post · Type @ to mention
        </p>
      </div>
    </div>
  )
}

export default CommentPanel
